'use client'

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/60 backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%]',
        // 3D elevated card
        // R4 SYSTEMIC: cap height + scroll the content so a tall modal never clips
        // its own footer (Submit/Save) on short viewports. Actions are always reachable.
        'rounded-2xl border-[1.5px] border-[hsl(var(--modal-border))] bg-[hsl(var(--modal-bg))] text-[hsl(var(--modal-fg))]',
        'max-h-[90dvh] overflow-y-auto overflow-x-hidden',
        'gap-0 p-6',
        'shadow-[0_25px_60px_-12px_rgba(0,0,0,0.35),0_0_0_1px_rgba(255,255,255,0.05)_inset,0_1px_2px_0_rgba(255,255,255,0.1)_inset]',
        'dark:shadow-[0_25px_60px_-12px_rgba(0,0,0,0.65),0_0_0_1px_rgba(255,255,255,0.08)_inset,0_1px_2px_0_rgba(255,255,255,0.06)_inset]',
        // Animation
        'duration-300 ease-out',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className={cn(
        'absolute right-3 top-3 z-10',
        'flex h-7 w-7 items-center justify-center rounded-lg',
        'bg-foreground/5 text-foreground/60',
        'transition-all hover:bg-foreground/10 hover:text-foreground',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
        'disabled:pointer-events-none',
      )}>
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

// ─── Gradient variant type ───────────────────────────────────────────────────
type GradientVariant = 'emerald' | 'amber' | 'red' | 'blue' | 'indigo' | 'purple' | 'cyan' | 'rose' | 'teal' | 'orange'

const GRADIENT_MAP: Record<GradientVariant, string> = {
  emerald: 'from-emerald-500 via-emerald-600 to-emerald-700',
  amber:   'from-amber-500 via-amber-600 to-amber-700',
  red:     'from-red-500 via-red-600 to-red-700',
  blue:    'from-blue-500 via-blue-600 to-blue-700',
  indigo:  'from-indigo-500 via-indigo-600 to-indigo-700',
  purple:  'from-purple-500 via-purple-600 to-purple-700',
  cyan:    'from-cyan-500 via-cyan-600 to-cyan-700',
  rose:    'from-rose-500 via-rose-600 to-rose-700',
  teal:    'from-teal-500 via-teal-600 to-teal-700',
  orange:  'from-orange-500 via-orange-600 to-orange-700',
}

// ─── 3D Gradient DialogHeader ────────────────────────────────────────────────
// W38-159: `icon` accepts either an ElementType (e.g. <ShieldAlert /> the component
// itself — as CreateModal passes it) OR a ReactNode (an already-rendered element).
// Previously the prop was typed React.ReactNode but the consumers (CreateModal) pass
// the component reference, which React tried to render as data and threw error #31
// ("object with keys $$typeof, render, displayName"). We now detect a function/class
// and wrap it as <Icon /> when needed.
interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  gradient?: GradientVariant | 'none'
  icon?: React.ElementType | React.ReactNode
}

function renderHeaderIcon(icon: React.ElementType | React.ReactNode): React.ReactNode {
  if (!icon) return null
  // Already a valid React element (e.g. <Icon />) → render as-is
  if (React.isValidElement(icon)) return icon
  // Otherwise treat as ElementType: plain function component OR forwardRef/memo
  // wrapper object (Lucide icons are forwardRef objects with {$$typeof, render, displayName})
  if (typeof icon === 'function' || (typeof icon === 'object' && icon !== null)) {
    const Icon = icon as React.ElementType
    return <Icon className="h-5 w-5 text-white" />
  }
  return icon as React.ReactNode
}

const DialogHeader = ({
  className,
  gradient,
  icon,
  children,
  ...props
}: DialogHeaderProps) => {
  // Modern default: every header gets a gradient strip unless explicitly opted out via gradient="none"
  const resolved: GradientVariant | 'none' = gradient ?? 'blue'
  if (resolved !== 'none') {
    const gradientClasses = GRADIENT_MAP[resolved as GradientVariant] || GRADIENT_MAP.blue
    return (
      <div
        className={cn(
          'relative overflow-hidden rounded-t-[inherit] px-6 pt-6 pb-5',
          '-mx-6 -mt-6 mb-4',
          'bg-gradient-to-br',
          gradientClasses,
          className
        )}
        {...props}
      >
        {/* 3D depth radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_20%,rgba(255,255,255,0.25)_0%,transparent_60%)]" />
        {/* Bottom edge highlight */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-white/20" />

        <div className="relative flex items-start gap-3">
          {icon && (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm ring-1 ring-white/20">
              {renderHeaderIcon(icon)}
            </div>
          )}
          <div className="flex flex-col space-y-1 text-white [&_*]:text-white">
            {children}
          </div>
        </div>
      </div>
    )
  }

  // Opt-out: plain header with border-bottom for visual separation
  return (
    <div
      className={cn(
        'flex flex-col space-y-1.5 px-6 pt-6 pb-4 text-left border-b border-border/60',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}
DialogHeader.displayName = 'DialogHeader'

// ─── 3D Gradient DialogFooter ────────────────────────────────────────────────
const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      'border-t border-border/40 bg-gradient-to-t from-muted/50 to-transparent',
      '-mx-6 -mb-6 mt-4 px-6 py-4',
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

// ─── DialogBody (new helper for padded content area) ─────────────────────────
const DialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('px-6 py-4', className)}
    {...props}
  />
)
DialogBody.displayName = 'DialogBody'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogBody,
  DialogTitle,
  DialogDescription,
}
export type { GradientVariant }
